import React, { useEffect, useRef } from 'react';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { XIcon } from 'lucide-react';
import type { LegalDoc } from '../../data/legal';
type LegalModalProps = {
  doc: LegalDoc | null;
  onClose: () => void;
};
export function LegalModal({
  doc,
  onClose
}: LegalModalProps) {
  const reduce = useReducedMotion();
  const closeRef = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    if (!doc) return;
    const previousOverflow = document.body.style.overflow;
    const previouslyFocused = document.activeElement as HTMLElement | null;
    document.body.style.overflow = 'hidden';
    closeRef.current?.focus({
      preventScroll: true
    });
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener('keydown', onKey);
      previouslyFocused?.focus({
        preventScroll: true
      });
    };
  }, [doc, onClose]);
  return <AnimatePresence data-id="element-134" data-mp="2i5eti72up95e">
      {doc && <motion.div className="fixed inset-0 z-[70] flex items-center justify-center bg-[#5B4380]/55 p-4 backdrop-blur-[2px]" initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} transition={{
      duration: reduce ? 0 : 0.2,
      ease: [0.23, 1, 0.32, 1]
    }} onClick={onClose} data-id="element-135" data-mp="2dd3vj9ro7xfh">
          <motion.div role="dialog" aria-modal="true" aria-labelledby={`legal-title-${doc.id}`} className="relative flex max-h-[85vh] w-full max-w-3xl flex-col rounded-[1.75rem] bg-white shadow-[0_30px_60px_-20px_rgba(60,30,90,0.45)]" initial={{
        opacity: 0,
        scale: 0.96,
        y: 8
      }} animate={{
        opacity: 1,
        scale: 1,
        y: 0
      }} exit={{
        opacity: 0,
        scale: 0.96,
        y: 8
      }} transition={{
        duration: reduce ? 0 : 0.22,
        ease: [0.23, 1, 0.32, 1]
      }} onClick={e => e.stopPropagation()} data-id="element-136" data-mp="31268w5f9v446">
            <button ref={closeRef} type="button" onClick={onClose} aria-label="Bezárás" className="absolute right-4 top-4 flex h-10 w-10 items-center justify-center rounded-full border border-lavender-line bg-lavender-soft text-ink transition-colors duration-150 hover:bg-lavender-line focus:outline-none focus-visible:ring-2 focus-visible:ring-lavender-deep" data-id="element-137" data-mp="1d68fu0ijyfzv">
              <XIcon className="h-4 w-4" aria-hidden="true" data-id="element-138" data-mp="z55slkftmznd" />
            </button>

            <div className="overflow-y-auto overscroll-contain px-6 pb-8 pt-8 sm:px-9" data-id="element-139" data-mp="z78ub1qc6h7x">
              <h2 id={`legal-title-${doc.id}`} className="pr-12 text-xl font-bold text-ink sm:text-[1.4rem]" data-id="element-140" data-mp="ki2ubbjocmyz">
                {doc.title}
              </h2>
              <div className="mt-4 space-y-4 text-[16px] leading-[1.8] text-body" data-id="element-141" data-mp="bfbg53zezd3n">
                {doc.paragraphs.map((p, i) => <p key={i} data-id="element-142" data-mp="ge4xx8lqhq9m" data-mpi={i}>
                    {p.label && <strong className="font-bold text-ink" data-id="element-143" data-mp="3sy0ksd6ldwtx">{p.label}</strong>}
                    {p.label && p.text && ' '}
                    {p.text}
                  </p>)}
              </div>
            </div>
          </motion.div>
        </motion.div>}
    </AnimatePresence>;
}